import React, { useState, useEffect } from 'react';
import './Success.css';

function Success() {
    const [loading, setLoading] = useState(true);
    const [orderDetails, setOrderDetails] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Get session_id from URL
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (!sessionId) {
            setError('No session ID found');
            setLoading(false);
            return;
        }

        // Fetch order details and generate license
        fetchOrderDetails(sessionId);
    }, []);

    const fetchOrderDetails = async (sessionId) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '/api';
            const endpoint = apiUrl.endsWith('/api') ? `${apiUrl}/generate-license` : `${apiUrl}/generate-license.php`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ session_id: sessionId }),
            });

            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setOrderDetails(data);
            }
        } catch (err) {
            setError('Failed to retrieve order details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const downloadLicense = () => {
        if (!orderDetails || !orderDetails.license_key) return;

        const blob = new Blob([orderDetails.license_key], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'batchmark-pdf-license.key';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const copyToClipboard = () => {
        if (!orderDetails || !orderDetails.license_key) return;

        navigator.clipboard.writeText(orderDetails.license_key);
        alert('License key copied to clipboard!');
    };

    if (loading) {
        return (
            <div className="success-page">
                <div className="success-container loading">
                    <div className="spinner"></div>
                    <h2>Processing your order...</h2>
                    <p>Please wait while we generate your license key.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="success-page">
                <div className="success-container error">
                    <div className="icon">❌</div>
                    <h1>Something went wrong</h1>
                    <p>{error}</p>
                    <p className="help-text">
                        If you completed payment, don't worry! Your license will be emailed to you shortly.
                        <br />
                        Contact support if you need assistance: support@batchmarkpdf.com
                    </p>
                    <button onClick={() => window.location.href = '/'}>Return to Home</button>
                </div>
            </div>
        );
    }

    return (
        <div className="success-page">
            <div className="success-container">
                <div className="icon success-icon">🎉</div>
                <h1>Thank you for your purchase!</h1>
                <p className="subtitle">Your license has been generated successfully</p>

                <div className="order-details">
                    <h2>Order Details</h2>
                    <div className="detail-row">
                        <span className="label">Order ID:</span>
                        <span className="value">{orderDetails.order_id}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Customer ID:</span>
                        <span className="value">{orderDetails.customer_id}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Email:</span>
                        <span className="value">{orderDetails.email}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">License Type:</span>
                        <span className="value">{orderDetails.product_type === 'business' ? 'Business' : 'Personal'}</span>
                    </div>
                </div>

                <div className="license-section">
                    <h2>Your License Key</h2>
                    <div className="license-key-box">
                        <code>{orderDetails.license_key}</code>
                    </div>
                    <div className="action-buttons">
                        <button className="primary-button" onClick={downloadLicense}>
                            📥 Download License File
                        </button>
                        <button className="secondary-button" onClick={copyToClipboard}>
                            📋 Copy to Clipboard
                        </button>
                    </div>
                </div>

                <div className="email-notice">
                    <h3>📧 Check your email</h3>
                    <p>
                        A copy of your license key and order details has been sent to <strong>{orderDetails.email}</strong>
                    </p>
                </div>

                <div className="next-steps">
                    <h2>Next Steps</h2>
                    <ol>
                        <li>Download your license file using the button above</li>
                        <li>Download BatchMark PDF for your platform (check your email for links)</li>
                        <li>Place the license file in the same directory as the application</li>
                        <li>Launch BatchMark PDF and enjoy all premium features!</li>
                    </ol>
                </div>

                <div className="support-section">
                    <p>Need help? Contact us at <a href="mailto:support@batchmarkpdf.com">support@batchmarkpdf.com</a></p>
                    <button className="link-button" onClick={() => window.location.href = '/'}>
                        ← Return to Home
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Success;
